import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { fmtCurrency, fmtNumber } from "../lib/format";
import { TopBar } from "../components/TopBar";
import { EditableCell } from "../components/EditableCell";
import { PaymentsSection } from "../components/PaymentsSection";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Plus, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "../lib/utils";

export default function BrandDetailPage() {
  const { brandId } = useParams();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingRow, setAddingRow] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/brands/${brandId}`);
      setDetail(res.data);
    } catch (e) {
      toast.error("Failed to load brand");
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    load();
  }, [load]);

  const saveRowField = async (rowId, field, value) => {
    try {
      const res = await api.patch(`/brands/${brandId}/rows/${rowId}`, { [field]: value });
      setDetail(res.data);
    } catch (e) {
      toast.error("Failed to save. Value reverted.");
      load();
    }
  };

  const saveBrandField = async (field, value) => {
    try {
      const res = await api.patch(`/brands/${brandId}`, { [field]: value });
      setDetail(res.data);
    } catch (e) {
      toast.error("Failed to save. Value reverted.");
      load();
    }
  };

  const addRow = async (week) => {
    // Only accept explicit valid values; anything else (e.g. a click event) means "current week"
    const weekOverride = week === "previous" || week === "current" ? week : null;
    setAddingRow(true);
    try {
      const res = await api.post(`/brands/${brandId}/rows`, weekOverride ? { week: weekOverride } : {});
      setDetail(res.data);
    } catch (e) {
      toast.error("Failed to add row");
    } finally {
      setAddingRow(false);
    }
  };

  const confirmWeek = async () => {
    try {
      const res = await api.post(`/brands/${brandId}/confirm-week`);
      setDetail(res.data);
      toast.success("Week confirmed — archived and Opening Balance carried forward");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to confirm week");
    }
  };

  const addPayment = async (date, amount, note) => {
    try {
      const res = await api.post(`/brands/${brandId}/payments`, { date, amount, note });
      setDetail(res.data);
      toast.success("Payment logged");
    } catch (e) {
      toast.error("Failed to add payment");
    }
  };

  const updatePayment = async (paymentId, field, value) => {
    try {
      const res = await api.patch(`/brands/${brandId}/payments/${paymentId}`, { [field]: value });
      setDetail(res.data);
    } catch (e) {
      toast.error("Failed to save payment. Value reverted.");
      load();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-muted/30">
        <TopBar crumbs={[{ label: "Brands", to: "/brands" }]} />
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="min-h-screen bg-muted/30">
        <TopBar crumbs={[{ label: "Brands", to: "/brands" }]} />
        <div className="mx-auto max-w-7xl px-4 py-16 text-center text-muted-foreground">Brand not found.</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <TopBar crumbs={[{ label: "Brands", to: "/brands" }, { label: detail.name }]} />
      <main className="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h1 data-testid="brand-name-heading" className="text-2xl font-semibold tracking-tight sm:text-3xl">
            {detail.name}
          </h1>
          <div className="flex flex-wrap items-center gap-3">
            {detail.week_confirmed ? (
              <div
                data-testid="brand-week-confirmed-badge"
                className="flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700"
              >
                <CheckCircle2 className="h-4 w-4" />
                Week confirmed{detail.confirmed_by ? ` by ${detail.confirmed_by}` : ""}
              </div>
            ) : (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" data-testid="brand-confirm-week-button">
                    <CheckCircle2 className="mr-1.5 h-4 w-4" />
                    Confirm calculation
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="brand-confirm-week-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Confirm calculation</AlertDialogTitle>
                    <AlertDialogDescription>
                      Close this brand's accounting week now? Its confirmed data will be archived and the final
                      Total becomes the new Opening Balance. The automatic Tuesday rollover will skip this brand.
                      Activity belonging to the new week is kept.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="brand-confirm-week-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={confirmWeek} data-testid="brand-confirm-week-confirm">
                      Yes, confirm and roll over
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <div className="rounded-lg border border-primary/30 bg-accent/40 px-4 py-2" data-testid="brand-available-balance-banner">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Available Balance</span>
              <span
                data-testid="brand-available-balance-value"
                className={cn(
                  "ml-3 font-mono text-xl font-semibold tabular-nums",
                  detail.total < 0 ? "text-red-600" : "text-accent-foreground"
                )}
              >
                {fmtCurrency(detail.total)}
              </span>
            </div>
          </div>
        </div>

        {/* TABLE 1 — TRAFFIC DATA */}
        <Card className="shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">Traffic Data</CardTitle>
            <p className="text-xs text-muted-foreground">
              CRG is a manual % input. Gu. FTD's = CRG × Valid leads and Expense = -CPA × MAX(Gu. FTD's, FTD's) update automatically.
            </p>
          </CardHeader>
          <CardContent>
            <div className="w-full overflow-x-auto rounded-md border border-border">
              <Table data-testid="brand-traffic-table" className="min-w-[1100px]">
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-[120px]">Geo</TableHead>
                    <TableHead className="w-[130px] text-right">Total leads</TableHead>
                    <TableHead className="w-[130px] text-right">Valid leads</TableHead>
                    <TableHead className="w-[110px] text-right">CRG</TableHead>
                    <TableHead className="w-[120px] text-right">CPA</TableHead>
                    <TableHead className="w-[120px] text-right">Gu. FTD's</TableHead>
                    <TableHead className="w-[120px] text-right">FTD's</TableHead>
                    <TableHead className="w-[140px] text-right">Expense</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detail.rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="py-8 text-center text-sm text-muted-foreground">
                        No rows yet. Click "+ Add Row" to start entering traffic data.
                      </TableCell>
                    </TableRow>
                  )}
                  {detail.rows.map((row, idx) => (
                    <TableRow key={row.id} className="transition-colors duration-150 hover:bg-muted/40">
                      <TableCell className="p-1.5">
                        <EditableCell
                          type="text"
                          value={row.geo}
                          placeholder="US"
                          testId={`brand-row-${idx}-geo-input`}
                          onSave={(v) => saveRowField(row.id, "geo", v)}
                        />
                      </TableCell>
                      <TableCell className="p-1.5">
                        <EditableCell
                          value={row.total_leads}
                          testId={`brand-row-${idx}-total-leads-input`}
                          onSave={(v) => saveRowField(row.id, "total_leads", v)}
                        />
                      </TableCell>
                      <TableCell className="p-1.5">
                        <EditableCell
                          value={row.valid_leads}
                          testId={`brand-row-${idx}-valid-leads-input`}
                          onSave={(v) => saveRowField(row.id, "valid_leads", v)}
                        />
                      </TableCell>
                      <TableCell className="p-1.5">
                        <EditableCell
                          value={row.crg}
                          suffix="%"
                          placeholder="0"
                          testId={`brand-row-${idx}-crg-input`}
                          onSave={(v) => saveRowField(row.id, "crg", v)}
                        />
                      </TableCell>
                      <TableCell className="p-1.5">
                        <EditableCell
                          value={row.cpa}
                          testId={`brand-row-${idx}-cpa-input`}
                          onSave={(v) => saveRowField(row.id, "cpa", v)}
                        />
                      </TableCell>
                      <TableCell
                        className="bg-muted/50 text-right font-mono text-sm tabular-nums text-muted-foreground"
                        data-testid={`brand-row-${idx}-gu-ftd-cell`}
                      >
                        {fmtNumber(row.gu_ftd)}
                      </TableCell>
                      <TableCell className="p-1.5">
                        <EditableCell
                          value={row.ftd}
                          testId={`brand-row-${idx}-ftd-input`}
                          onSave={(v) => saveRowField(row.id, "ftd", v)}
                        />
                      </TableCell>
                      <TableCell
                        className={cn(
                          "bg-muted/50 text-right font-mono text-sm tabular-nums",
                          row.expense < 0 ? "text-red-600" : "text-foreground"
                        )}
                        data-testid={`brand-row-${idx}-expense-cell`}
                      >
                        {fmtCurrency(row.expense)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <Button
              variant="secondary"
              className="mt-3"
              onClick={() => addRow()}
              disabled={addingRow}
              data-testid="brand-traffic-add-row-button"
            >
              {addingRow ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />}
              Add Row
            </Button>
          </CardContent>
        </Card>

        {/* TABLE 2 — BRAND BALANCE */}
        <Card className="shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">Brand Balance</CardTitle>
            <p className="text-xs text-muted-foreground">
              Payments auto-sums the payment log below. Expenses auto-sums Table 1. Total = Opening balance + Payments + Expenses.
            </p>
          </CardHeader>
          <CardContent>
            <div className="w-full overflow-x-auto rounded-md border border-border">
              <Table data-testid="brand-balance-table" className="min-w-[400px]">
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Reason</TableHead>
                    <TableHead className="w-[220px] text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">Opening balance</TableCell>
                    <TableCell className="p-1.5">
                      <EditableCell
                        value={detail.opening_balance}
                        allowNegative
                        testId="brand-opening-balance-input"
                        onSave={(v) => saveBrandField("opening_balance", v)}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Payments</TableCell>
                    <TableCell
                      data-testid="brand-payments-cell"
                      className={cn(
                        "bg-muted/50 text-right font-mono tabular-nums",
                        detail.payments < 0 ? "text-red-600" : "text-foreground"
                      )}
                    >
                      {fmtCurrency(detail.payments)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Expenses</TableCell>
                    <TableCell
                      data-testid="brand-expenses-cell"
                      className={cn(
                        "bg-muted/50 text-right font-mono tabular-nums",
                        detail.expenses < 0 ? "text-red-600" : "text-foreground"
                      )}
                    >
                      {fmtCurrency(detail.expenses)}
                    </TableCell>
                  </TableRow>
                  <TableRow className="border-t-2 border-border bg-accent/30">
                    <TableCell className="font-semibold">Total</TableCell>
                    <TableCell
                      data-testid="brand-total-cell"
                      className={cn(
                        "text-right font-mono font-semibold tabular-nums",
                        detail.total < 0 ? "text-red-600" : "text-accent-foreground"
                      )}
                    >
                      {fmtCurrency(detail.total)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* PAYMENTS LOG */}
        <Card className="shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">Payments — This Week</CardTitle>
            <p className="text-xs text-muted-foreground">
              Log every top-up as a dated entry. The sum feeds the Payments row above automatically.
            </p>
          </CardHeader>
          <CardContent>
            <PaymentsSection
              entries={detail.payment_entries || []}
              total={detail.payments}
              onAdd={addPayment}
              onUpdate={updatePayment}
              testPrefix="brand"
            />
          </CardContent>
        </Card>

        <div className="rounded-lg border border-primary/30 bg-accent/40 p-4">
          <p className="text-sm font-medium text-muted-foreground">Available Balance</p>
          <p
            data-testid="brand-available-balance-bottom"
            className={cn(
              "mt-1 font-mono text-2xl font-semibold tabular-nums",
              detail.total < 0 ? "text-red-600" : "text-accent-foreground"
            )}
          >
            {fmtCurrency(detail.total)}
          </p>
        </div>
      </main>
    </div>
  );
}
